import React from 'react';
import { Check, Copy, KeyRound, Plus, Trash2 } from 'lucide-react';
import { AppConfirmDialog } from '../../../../components/AppConfirmDialog';
import { AppModal, AppModalHeader } from '../../../../components/AppModal';
import { InlineLoadingState } from '../../../../components/LoadingSpinner';
import { SettingsGroup } from '../integrations/integrationUi';
import {
  SETTINGS_BTN_OUTLINE,
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
} from '../../utils/settingsSectionStyles';
import { ApiQuickstart } from './ApiQuickstart';
import { ApiReference } from './ApiReference';
import { createApiKey, fetchApiKeys, revokeApiKey, type ApiKeySummary, type ApiKeysOverview } from './apiKeysClient';
import { API_SCOPE_LABELS_DE, API_SCOPE_PRESETS, API_SCOPES, type ApiScope, type ApiScopePresetId } from './apiScopes';

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
};

const SHORT_SCOPE_LABELS: Record<ApiScope, string> = {
  'projects:read': 'Projekte lesen',
  'projects:write': 'Projekte anlegen',
  'offers:read': 'Angebote lesen',
  'offers:write': 'Angebote schreiben',
  'documents:write': 'Dokumente hochladen',
};

export function ApiKeysSettingsSection() {
  const [overview, setOverview] = React.useState<ApiKeysOverview | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [secretOnce, setSecretOnce] = React.useState<{ name: string; secret: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = React.useState<ApiKeySummary | null>(null);
  const [revoking, setRevoking] = React.useState(false);
  const [copyHint, setCopyHint] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    setError(null);
    try {
      setOverview(await fetchApiKeys());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  const copyText = async (label: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyHint(label);
      window.setTimeout(() => setCopyHint(null), 2000);
    } catch {
      setError('In die Zwischenablage kopieren ist fehlgeschlagen.');
    }
  };

  const runRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    setError(null);
    try {
      await revokeApiKey(revokeTarget.id);
      setRevokeTarget(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRevoking(false);
    }
  };

  const canManage = overview?.canManage === true;

  return (
    <>
      <SettingsGroup title="Zugang" description="Instanz-ID und API-Schlüssel für eure Anbindung. Jeder Schlüssel hat eigene Berechtigungen.">
        <div className="space-y-5 px-5 py-4 text-sm text-app-text-muted">
          {loading ? (
            <InlineLoadingState label="Lade…" className="justify-start" />
          ) : error && !overview ? (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>
          ) : overview ? (
            <div className="space-y-5">
              <div className="space-y-2">
                <p className={SETTINGS_FIELD_LABEL}>Instanz-ID</p>
                <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
                  <code className="flex-1 min-w-0 text-xs bg-app-surface-muted border border-app-border rounded-lg px-3 py-2.5 break-all text-app-text">
                    {overview.instanceId}
                  </code>
                  <button
                    type="button"
                    className={`${SETTINGS_BTN_OUTLINE} shrink-0 gap-2`}
                    onClick={() => void copyText('Instanz-ID', overview.instanceId)}
                  >
                    {copyHint === 'Instanz-ID' ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    {copyHint === 'Instanz-ID' ? 'Kopiert' : 'Kopieren'}
                  </button>
                </div>
              </div>

              {error ? <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div> : null}

              {secretOnce ? (
                <div className="rounded-lg border border-app-border bg-app-surface-muted/80 p-4 space-y-3">
                  <p className="text-sm font-medium text-app-text">Neuer API-Schlüssel „{secretOnce.name}“</p>
                  <p className="text-xs text-app-text-subtle">
                    Sichtbar nur jetzt. Bitte sicher speichern (z. B. als Secret in Lovable, Vercel oder Doppler). Danach ist er nicht mehr abrufbar.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
                    <code className="flex-1 min-w-0 text-xs bg-app-surface border border-app-border rounded-lg px-3 py-2.5 break-all text-app-text font-mono">
                      {secretOnce.secret}
                    </code>
                    <button
                      type="button"
                      className={`${SETTINGS_BTN_PRIMARY} shrink-0 gap-2`}
                      onClick={() => void copyText('API-Schlüssel', secretOnce.secret)}
                    >
                      {copyHint === 'API-Schlüssel' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copyHint === 'API-Schlüssel' ? 'Kopiert' : 'Schlüssel kopieren'}
                    </button>
                  </div>
                  <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={() => setSecretOnce(null)}>
                    Ausblenden
                  </button>
                </div>
              ) : null}

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className={SETTINGS_FIELD_LABEL}>API-Schlüssel</p>
                  {canManage ? (
                    <button type="button" className={`${SETTINGS_BTN_PRIMARY} gap-2`} onClick={() => setCreateOpen(true)}>
                      <Plus className="w-4 h-4" />
                      Neuen Schlüssel erstellen
                    </button>
                  ) : null}
                </div>
                {overview.keys.length === 0 ? (
                  <p className="text-sm text-app-text-subtle">Noch kein Schlüssel. Ohne Schlüssel ist die API für diese Instanz deaktiviert.</p>
                ) : (
                  <ul className="divide-y divide-app-border/60 rounded-lg border border-app-border">
                    {overview.keys.map((key) => (
                      <li key={key.id} className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0 space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <KeyRound className="w-4 h-4 text-app-text-subtle" />
                            <span className="font-medium text-app-text">{key.name}</span>
                            <code className="text-xs text-app-text-subtle font-mono">
                              {key.legacy ? 'aus der alten Lead-API' : `${key.key_prefix}…`}
                            </code>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {key.scopes.map((scope) => (
                              <span
                                key={scope}
                                title={API_SCOPE_LABELS_DE[scope]}
                                className="rounded-full bg-app-surface-muted px-2 py-0.5 text-[11px] font-medium text-app-text-muted"
                              >
                                {SHORT_SCOPE_LABELS[scope]}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-app-text-subtle">
                            Erstellt {formatDateTime(key.created_at)} · Zuletzt verwendet {formatDateTime(key.last_used_at)}
                            {key.expires_at ? ` · Läuft ab ${formatDateTime(key.expires_at)}` : ''}
                          </p>
                        </div>
                        {canManage ? (
                          <button
                            type="button"
                            className={`${SETTINGS_BTN_OUTLINE} shrink-0 gap-2 text-red-700`}
                            onClick={() => setRevokeTarget(key)}
                          >
                            <Trash2 className="w-4 h-4" />
                            Widerrufen
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
                {!canManage ? (
                  <p className="text-xs text-app-text-subtle">Nur Besitzer oder Administrator können Schlüssel erstellen und widerrufen.</p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </SettingsGroup>

      <ApiQuickstart instanceId={overview?.instanceId ?? null} />
      <ApiReference />

      <CreateApiKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(name, secret) => {
          setCreateOpen(false);
          setSecretOnce({ name, secret });
          void reload();
        }}
      />

      <AppConfirmDialog
        open={revokeTarget != null}
        onClose={() => (revoking ? undefined : setRevokeTarget(null))}
        title={`Schlüssel „${revokeTarget?.name ?? ''}“ widerrufen?`}
        description="Anfragen mit diesem Schlüssel werden sofort abgelehnt. Integrationen, die ihn verwenden, brauchen einen neuen Schlüssel."
        confirmLabel="Widerrufen"
        destructive
        isLoading={revoking}
        onConfirm={runRevoke}
      />
    </>
  );
}

function CreateApiKeyModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string, secret: string) => void;
}) {
  const [name, setName] = React.useState('');
  const [preset, setPreset] = React.useState<ApiScopePresetId | 'custom'>('read_plus_create_projects');
  const [scopes, setScopes] = React.useState<ApiScope[]>(
    [...(API_SCOPE_PRESETS.find((p) => p.id === 'read_plus_create_projects')?.scopes ?? [])],
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setPreset('read_plus_create_projects');
    setScopes([...(API_SCOPE_PRESETS.find((p) => p.id === 'read_plus_create_projects')?.scopes ?? [])]);
    setError(null);
  }, [open]);

  const choosePreset = (id: ApiScopePresetId | 'custom') => {
    setPreset(id);
    if (id === 'custom') return;
    const found = API_SCOPE_PRESETS.find((p) => p.id === id);
    if (found) setScopes([...found.scopes]);
  };

  const toggleScope = (scope: ApiScope) => {
    setPreset('custom');
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Bitte einen Namen vergeben, z. B. „Website-Formular“ oder „Baustellen-App“.');
      return;
    }
    if (scopes.length === 0) {
      setError('Mindestens eine Berechtigung auswählen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createApiKey({ name: trimmed, scopes });
      onCreated(created.key.name, created.secret);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} panelClassName="md:max-w-lg" disableClose={saving} dirty={name.trim().length > 0} ariaLabelledBy="api-key-create-title">
      {(requestClose) => (
        <>
          <AppModalHeader title="Neuen API-Schlüssel erstellen" titleId="api-key-create-title" onClose={requestClose} closeDisabled={saving} />
          <div className="space-y-5 px-5 py-4 text-sm">
            <label className="block space-y-1.5">
              <span className={SETTINGS_FIELD_LABEL}>Name</span>
              <input
                type="text"
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
                placeholder="z. B. Baustellen-App von Robert"
                className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-sm text-app-text"
                autoFocus
              />
            </label>

            <div className="space-y-2">
              <span className={SETTINGS_FIELD_LABEL}>Berechtigungen</span>
              <div className="flex flex-wrap gap-2">
                {API_SCOPE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => choosePreset(p.id)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium ${
                      preset === p.id ? 'border-app-accent bg-app-accent/10 text-app-text' : 'border-app-border text-app-text-muted hover:bg-app-hover'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => choosePreset('custom')}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${
                    preset === 'custom' ? 'border-app-accent bg-app-accent/10 text-app-text' : 'border-app-border text-app-text-muted hover:bg-app-hover'
                  }`}
                >
                  Individuell
                </button>
              </div>
              <ul className="space-y-1.5 rounded-lg border border-app-border p-3">
                {API_SCOPES.map((scope) => (
                  <li key={scope}>
                    <label className="flex items-start gap-2 text-sm text-app-text">
                      <input type="checkbox" className="mt-0.5" checked={scopes.includes(scope)} onChange={() => toggleScope(scope)} />
                      <span>
                        {API_SCOPE_LABELS_DE[scope]} <code className="ml-1 text-[11px] text-app-text-subtle">{scope}</code>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-app-text-subtle">
                So wenig wie nötig: eine Website braucht nur „Projekte anlegen“, eine Baustellen-App meist „Lesen + Projekte anlegen“.
              </p>
            </div>

            {error ? <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div> : null}
          </div>
          <div className="flex flex-col-reverse gap-2 border-t border-app-border px-5 py-4 sm:flex-row sm:justify-end">
            <button type="button" className={SETTINGS_BTN_OUTLINE} onClick={requestClose} disabled={saving}>
              Abbrechen
            </button>
            <button type="button" className={SETTINGS_BTN_PRIMARY} onClick={() => void submit()} disabled={saving}>
              {saving ? 'Erstellt…' : 'Schlüssel erstellen'}
            </button>
          </div>
        </>
      )}
    </AppModal>
  );
}
